/**
 * 分期商城相关实验伽利略 AB 实验统一配置
 * 所有实验的 experimentId、defaultVersionId 仅在此处维护，业务通过实验 key 获取结果
 * @see https://ab.hellobike.cn/
 */
import { getGalileoAb } from '@/shared/galileoAb';
import type { AbTestArgs } from '@/api/user/types';

/** 单个实验配置 */
export interface AbExperimentConfig {
  /** 伽利略实验 ID */
  experimentId: string;
  /** 兜底版本 ID（一般为对照组，需从伽利略实验详情页获取） */
  defaultVersionId: string;
  /** 实验说明 / 文档链接，便于维护 */
  description?: string;
}

/**
 * 实验 Key 枚举 — 仅在此文件与调用方使用，避免魔法字符串
 */
export enum AbExperimentKey {
  // ========== 分期商城授信成功倒计时分流 ==========
  /** 商城授信结果页-授信成功倒计时分流 https://ab.hellobike.cn/experiment/detail/202602251128262653 */
  FQ_MALL_SUCCESS_COUNTDOWN = 'FQ_MALL_SUCCESS_COUNTDOWN',
  /** 授信失败/贷超半流程结果页-授信成功倒计时分流 https://ab.hellobike.cn/experiment/detail/202602251311096212 */
  PRE_APPLY_RESULT_MALL_SUCCESS_COUNTDOWN = 'PRE_APPLY_RESULT_MALL_SUCCESS_COUNTDOWN',
  /** 提现结果页-授信成功倒计时分流 https://ab.hellobike.cn/experiment/detail/202602251313490831 */
  APPLY_RESULT_MALL_SUCCESS_COUNTDOWN = 'APPLY_RESULT_MALL_SUCCESS_COUNTDOWN',
}

/** 全量实验配置表：key -> 伽利略参数 */
export const AB_EXPERIMENT_CONFIG: Record<AbExperimentKey, AbExperimentConfig> = {
  // ---------- 分期商城授信成功倒计时分流（需从伽利略获取对照组 defaultVersionId 后替换） ----------
  [AbExperimentKey.FQ_MALL_SUCCESS_COUNTDOWN]: {
    experimentId: '202602251128262653',
    defaultVersionId: '202602251128262787',
    description: '商城授信结果页-授信成功倒计时分流',
  },
  [AbExperimentKey.PRE_APPLY_RESULT_MALL_SUCCESS_COUNTDOWN]: {
    experimentId: '202602251311096212',
    defaultVersionId: '202602251311096323',
    description: '授信失败/贷超半流程结果页-授信成功倒计时分流',
  },
  [AbExperimentKey.APPLY_RESULT_MALL_SUCCESS_COUNTDOWN]: {
    experimentId: '202602251313490831',
    defaultVersionId: '202602251313490932',
    description: '提现结果页-授信成功倒计时分流',
  },
};

/**
 * 根据实验 key 获取实验结果（内部 catch 异常，不影响主流程）
 * @param key 实验枚举 key
 * @returns 伽利略返回的 versionArgs，异常时返回空对象
 */
export async function getAbResultByKey(key: AbExperimentKey): Promise<AbTestArgs> {
  const config = AB_EXPERIMENT_CONFIG[key];
  if (!config) {
    return {};
  }
  return getGalileoAb().getAbResCatchError(config.experimentId, config.defaultVersionId);
}

/**
 * 判断是否为「授信成功直跳」实验组（不做倒计时）
 * 约定：result === '1' 为实验组直跳，result === '0' 为对照组倒计时
 * @param args getAbResultByKey 返回的结果
 * @returns true 表示实验组，不展示倒计时、授信成功后直跳
 */
export function isSkipCountdownGroup(args: AbTestArgs): boolean {
  return String(args?.result) === '1';
}
